import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { track } from "@/lib/analytics";
import { supabase, type ClothingItem } from "@/lib/supabase";
import { useProfile } from "@/lib/useProfile";
import { AppShell } from "@/components/app-shell";
import { ImageBox } from "@/components/ui-extras";
import { bgColorFor } from "@/components/bg-preference-selector";
import { EmptyState } from "@/components/empty-state";
import { Shirt } from "lucide-react";
import { PhotoUpload } from "@/components/photo-upload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { FavoriteHeart, ItemActions } from "@/components/item-actions";
import { useCardReveal, CardRevealOverlay } from "@/components/card-reveal";
import { ClothingEditDialog } from "@/components/clothing-edit-dialog";
import { QuickAddSheet } from "@/components/quick-add-sheet";


import { useItemLimits, FREE_LIMITS } from "@/lib/useSubscription";
import { UpgradePromptSheet } from "@/components/upgrade-prompt-sheet";
import { CustomizeViewDialog, CLOTHING_FIELDS } from "@/components/customize-view-dialog";
import { NearEmptyBanner } from "@/components/collection-prompts";
import { KeyboardAwareFormBody } from "@/components/keyboard-aware-form-body";
import {
  SmartSelect,
  SmartMultiSelect,
  CLOTHING_TYPE_PRESETS,
  CLOTHING_COLOR_PRESETS,
  CLOTHING_PATTERN_PRESETS,
  CLOTHING_NECKLINE_PRESETS,
  CLOTHING_STORAGE_PRESETS,
  SHARED_OCCASION_PRESETS,
} from "@/components/smart-select";
import {
  CollectionToolbar,
  applyTextSearch,
  type ActiveFilters,
  type FilterFieldDef,
  type SortOption,
} from "@/components/collection-toolbar";
import { useMemo } from "react";
import { useOwnedClothing } from "@/lib/useCollection";
import { useClosetCollection } from "@/hooks/useClosetCollection";

export const Route = createFileRoute("/clothing/")({
  validateSearch: (s: Record<string, unknown>): { new?: number } => ({
    new: s.new === 1 || s.new === "1" ? 1 : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Clothing — Cailève" },
      { name: "description", content: "Wardrobe pieces that support your jewelry." },
    ],
  }),
  component: () => (
    <AppShell>
      <ClothingPage />
    </AppShell>
  ),
});

function ClothingPage() {
  const { user } = useAuth();
  if (!user) return null;
  const uid = user.id;
  const [open, setOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const limits = useItemLimits();
  const clothingUpgradeReason: "item_limit" | "item_limit_lapsed" =
    (limits.counts?.clothing ?? 0) > FREE_LIMITS.clothing
      ? "item_limit_lapsed"
      : "item_limit";
  const tryOpenAdd = () => {
    if (limits.canAddClothing) setOpen(true);
    else setUpgradeOpen(true);
  };
  const search = Route.useSearch();
  useEffect(() => {
    if (search.new === 1) tryOpenAdd();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.new, limits.canAddClothing]);
  const qc = useQueryClient();
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState<ActiveFilters>({});
  const [sortValue, setSortValue] = useState<string>("recent");
  const { data: profile } = useProfile();
  const visibleFields = profile?.clothing_card_fields ?? ["clothing_type"];

  const { data: crossCount } = useQuery({
    queryKey: ["clothing_cross_count", uid],
    staleTime: 2 * 60 * 1000,
    queryFn: async () => {
      const { count } = await supabase
        .from("jewelry_items")
        .select("id", { count: "exact", head: true })
        .eq("user_id", uid);
      return count ?? 0;
    },
  });

  const { data, isLoading } = useOwnedClothing();

const {
    wishlistItems: clothingWishlist,
    toggleFavorite: toggleFav,
    deleteItem: del,
    promoteFromWishlist: promoteClothingFromWishlist,
  } = useClosetCollection({ type: "clothing", uid });

  const deletingItem = data?.find((c) => c.id === deleteId) ?? null;
  const editingItem = data?.find((c) => c.id === editId) ?? null;

  const formatFieldValue = (c: ClothingItem, key: string): string | null => {
    switch (key) {
      case "clothing_type": return c.clothing_type;
      case "primary_color": return c.primary_color;
      case "pattern": return c.pattern;
      case "neckline": return c.neckline;
      case "price": return c.price != null ? `$${c.price}` : null;
      default: return null;
    }
  };

  const unique = (key: keyof ClothingItem): string[] =>
    Array.from(
      new Set(
        (data ?? [])
          .map((c) => c[key])
          .filter((v): v is string => typeof v === "string" && v.length > 0),
      ),
    ).sort();

  const filterFields: FilterFieldDef[] = useMemo(
    () => [
      { key: "storage_location", label: "Storage Location", type: "select", options: unique("storage_location") },
      { key: "clothing_type", label: "Type", type: "select", options: unique("clothing_type") },
      { key: "primary_color", label: "Color", type: "select", options: unique("primary_color") },
      { key: "pattern", label: "Pattern", type: "select", options: unique("pattern") },
      { key: "neckline", label: "Neckline", type: "select", options: unique("neckline") },
      { key: "occasion", label: "Occasion", type: "select", options: unique("occasion") },
      { key: "is_favorite", label: "Favorites only", type: "toggle" },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data],
  );

  const sortOptions: SortOption[] = [
    { value: "recent", label: "Recently Added" },
    { value: "name_asc", label: "Name A → Z" },
    { value: "name_desc", label: "Name Z → A" },
    { value: "clothing_type", label: "Type" },
    { value: "primary_color", label: "Color" },
    { value: "pattern", label: "Pattern" },
    { value: "price_asc", label: "Price Low → High" },
    { value: "price_desc", label: "Price High → Low" },
    { value: "favorites", label: "Favorites First" },
  ];

  const { data: wearCounts } = useQuery({
    queryKey: ["clothing_wear_counts", uid],
    enabled: !!uid,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data: logs } = await supabase
        .from("wear_logs")
        .select("item_id")
        .eq("user_id", uid)
        .eq("item_type", "clothing");
      const counts = new Map<string, number>();
      (logs ?? []).forEach((l) => counts.set(l.item_id, (counts.get(l.item_id) ?? 0) + 1));
      return counts;
    },
  });

  const displayItems = useMemo(() => {
    let items = data ?? [];
    if (filters.is_favorite) items = items.filter((c) => c.is_favorite);
    for (const k of ["clothing_type", "primary_color", "pattern", "neckline", "storage_location", "occasion"] as const) {
      const v = filters[k];
      if (typeof v === "string" && v) items = items.filter((c) => c[k] === v);
    }
    items = applyTextSearch(items, searchQuery, [
      "name",
      "clothing_type",
      "primary_color",
      "pattern",
      "neckline",
      "storage_location",
      "occasion",
    ]);
    const sorted = [...items];
    const cmp = (a?: string | null, b?: string | null) =>
      (a ?? "").localeCompare(b ?? "");
    switch (sortValue) {
      case "name_asc": sorted.sort((a, b) => cmp(a.name, b.name)); break;
      case "name_desc": sorted.sort((a, b) => cmp(b.name, a.name)); break;
      case "clothing_type": sorted.sort((a, b) => cmp(a.clothing_type?.toLowerCase().trim(), b.clothing_type?.toLowerCase().trim())); break;
      case "primary_color": sorted.sort((a, b) => cmp(a.primary_color?.toLowerCase().trim(), b.primary_color?.toLowerCase().trim())); break;
      case "pattern": sorted.sort((a, b) => cmp(a.pattern?.toLowerCase().trim(), b.pattern?.toLowerCase().trim())); break;
      case "price_asc": sorted.sort((a, b) => (a.price ?? Infinity) - (b.price ?? Infinity)); break;
      case "price_desc": sorted.sort((a, b) => (b.price ?? -Infinity) - (a.price ?? -Infinity)); break;
      case "favorites": sorted.sort((a, b) => Number(!!b.is_favorite) - Number(!!a.is_favorite)); break;
      default: sorted.sort((a, b) => (b.created_at ?? "").localeCompare(a.created_at ?? ""));
    }
    return sorted;
  }, [data, filters, searchQuery, sortValue]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-12">
      <div className="flex items-end justify-between border-b border-border pb-6 mb-6">
        <div>
          <h1 className="font-serif-display text-4xl">Your Clothing</h1>
          <p className="font-serif-display italic text-base mt-1" style={{ color: "var(--caileve-lilac)" }}>The pieces Cailève styles from first.</p>
          {data && (
            <p style={{ fontFamily: "Inter, sans-serif", fontSize: 11, letterSpacing: "0.25em", textTransform: "uppercase", color: "var(--caileve-muted)", marginTop: 6 }}>
              {(searchQuery || Object.values(filters).some((v) => v !== undefined && v !== "" && v !== false))
                ? `${displayItems.length} of ${data.length} pieces`
                : `${data.length} ${data.length === 1 ? "piece" : "pieces"}`}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <CustomizeViewDialog
            userId={uid}
            column="clothing_card_fields"
            options={CLOTHING_FIELDS}
            current={visibleFields}
            title="Customize Clothing Cards"
          />
          <Button
            type="button"
            onClick={tryOpenAdd}
            className="bg-[var(--caileve-gold)] text-[var(--caileve-bg)] hover:bg-[var(--caileve-gold)]/90"
            style={{ borderRadius: "var(--caileve-radius-pill)" }}
          >
            <Plus className="h-4 w-4 mr-2" /> Add Piece
          </Button>
          <QuickAddSheet
            open={open}
            onOpenChange={setOpen}
            userId={uid}
            defaultType="clothing"
            onDone={() => qc.invalidateQueries({ queryKey: ["clothing_items", uid] })}
          />
          <UpgradePromptSheet open={upgradeOpen} onOpenChange={setUpgradeOpen} reason={clothingUpgradeReason} />
        </div>
      </div>
      {/* Collection type switcher */}
      <div className="flex gap-2 mb-6">
        {([
          { to: "/jewelry" as const, label: "Jewelry", search: { new: undefined } as { new: number | undefined } },
          { to: "/clothing" as const, label: "Clothing", search: { new: undefined } as { new: number | undefined } },
          { to: "/accessories" as const, label: "Accessories", search: { new: undefined } as { new: number | undefined } },
        ] as const).map(({ to, label, search }) => {
          const pathname = window.location.pathname;
          const active = pathname === to || pathname.startsWith(to + "/");
          return (
            <Link
              key={to}
              to={to}
              search={search}
              className="px-4 text-sm transition-colors"
              style={
                active
                  ? {
                      backgroundColor: "transparent",
                      color: "var(--caileve-ink)",
                      borderBottom: "2px solid var(--caileve-gold)",
                      borderRadius: 0,
                      fontWeight: 600,
                      paddingBottom: 6,
                    }
                  : {
                      backgroundColor: "transparent",
                      color: "var(--caileve-muted)",
                      border: "none",
                      paddingBottom: 6,
                    }
              }
            >
              {label}
            </Link>
          );
        })}
      </div>
      {(clothingWishlist?.length ?? 0) > 0 &&
        localStorage.getItem("caileve_considering_prompt_dismissed_v1") !== "1" && (
        <button
          type="button"
          onClick={() => {
            document.getElementById("considering-section")?.scrollIntoView({ behavior: "smooth" });
          }}
          className="flex items-center justify-between w-full px-4 py-3 mb-4 text-left transition-opacity hover:opacity-80"
          style={{
            backgroundColor: "var(--caileve-soft)",
            borderLeft: "3px solid var(--caileve-gold)",
          }}
        >
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em]" style={{ color: "var(--caileve-muted)" }}>
              Your Edit
            </p>
            <p className="font-serif-display text-lg mt-0.5" style={{ color: "var(--caileve-ink)" }}>
              {clothingWishlist!.length} {clothingWishlist!.length === 1 ? "piece" : "pieces"} in Your Edit
            </p>
            <p
              style={{
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                fontStyle: "italic",
                fontSize: 13,
                color: "var(--caileve-muted)",
                marginTop: 2,
              }}
            >
              Pieces you are still deciding on.
            </p>
          </div>
          <span className="text-[var(--caileve-muted)] text-sm">↓</span>
        </button>
      )}


      {isLoading ? (
        <CollectionSkeletonGrid count={(data as unknown[] | undefined)?.length || 8} />
      ) : !data?.length ? (
        <NearEmptyBanner kind="clothing" itemCount={0} onCta={tryOpenAdd} crossCount={crossCount ?? 0} />
      ) : (
        <>
          <CollectionToolbar
            searchPlaceholder="Search your wardrobe…"
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            filterFields={filterFields}
            activeFilters={filters}
            onFilterChange={(k, v) => setFilters((p) => ({ ...p, [k]: v }))}
            onClearFilters={() => setFilters({})}
            sortOptions={sortOptions}
            sortValue={sortValue}
            onSortChange={setSortValue}
          />
          {displayItems.length === 0 ? (
            <div className="border border-dashed border-[var(--caileve-soft)] py-24 text-center">
              <p
                className="font-serif-display text-2xl"
                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: "var(--caileve-lilac)" }}
              >
                No pieces match your search.
              </p>
              <button
                type="button"
                onClick={() => { setSearchQuery(""); setFilters({}); }}
                className="mt-6 px-6 py-2 text-xs uppercase tracking-[0.25em] text-[var(--caileve-bg)]"
                style={{ backgroundColor: "var(--caileve-lilac)" }}
              >
                Clear Filters
              </button>
            </div>
          ) : (
        <div className="collection-content grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {displayItems.map((c) => (
            <ClothingGridCard
              key={c.id}
              item={c}
              visibleFields={visibleFields}
              formatFieldValue={formatFieldValue}
              wearCount={wearCounts?.get(c.id) ?? 0}
              onToggleFav={() => toggleFav.mutate({ id: c.id, next: !c.is_favorite })}
              onEdit={() => setEditId(c.id)}
              onDelete={() => setDeleteId(c.id)}
            />
          ))}
        </div>
          )}
        </>
      )}

      {clothingWishlist && clothingWishlist.length > 0 && (
        <section id="considering-section" className="mt-16">
          <div
            style={{
              height: 1,
              backgroundColor: "var(--caileve-gold)",
              width: "100%",
              marginBottom: 32,
            }}
          />
          <h2
            style={{
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              fontSize: 24,
              color: "var(--caileve-muted)",
            }}
          >
            Your Edit
          </h2>
          <p
            style={{
              fontSize: 13,
              fontStyle: "italic",
              color: "var(--caileve-muted)",
              marginTop: 4,
              marginBottom: 24,
            }}
          >
            Pieces you're thinking about.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {clothingWishlist.map((w) => (
              <div key={w.id}>
                <Link
                  to="/clothing/$id"
                  params={{ id: w.id }}
                  className="block cursor-pointer"
                >
                  <ImageBox src={w.photo_url} alt={w.name} />
                  <div className="mt-3">
                    <p className="font-serif-display text-xl leading-tight line-clamp-2">
                      {w.name}
                    </p>
                  </div>
                </Link>
                <button
                  type="button"
                  onClick={() => promoteClothingFromWishlist.mutate(w.id)}
                  disabled={promoteClothingFromWishlist.isPending}
                  style={{
                    fontSize: 11,
                    color: "var(--caileve-lilac)",
                    textDecoration: "underline",
                    background: "none",
                    border: "none",
                    padding: "6px 0 0",
                    cursor: "pointer",
                  }}
                >
                  Add to my collection
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this garment?</AlertDialogTitle>
            <AlertDialogDescription>
              {deletingItem
                ? `"${deletingItem.name}" will be permanently removed from your wardrobe.`
                : "This garment will be permanently removed."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={del.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={del.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (deleteId) {
                  del.mutate(deleteId, {
                    onSuccess: () => setDeleteId(null),
                  });
                }
              }}
            >
              {del.isPending ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!editId} onOpenChange={(o) => !o && setEditId(null)}>
        {editingItem && (
          <ClothingEditDialog
            item={editingItem}
            userId={uid}
            onDone={() => setEditId(null)}
          />
        )}
      </Dialog>
    </div>
  );
}

const EMPTY_CLOTHING = {
  name: "",
  photo_url: "",
  clothing_type: "",
  primary_color: "",
  secondary_color: "",
  pattern: "",
  neckline: "",
  price: "",
  storage_location: "",
  occasion: "",
  is_favorite: false,
};

function AddClothingDialog({
  open,
  userId,
  onDone,
}: {
  open: boolean;
  userId: string;
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const [f, setF] = useState({ ...EMPTY_CLOTHING });
  useEffect(() => {
    if (open) setF({ ...EMPTY_CLOTHING });
  }, [open]);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) =>
    setF((p) => ({ ...p, [k]: v }));

  // Single bulk fetch of all clothing user_field_options — replaces the
  // per-SmartSelect N+1 fetches that previously fired one query per field.
  const CLOTHING_FIELD_NAMES = [
    "clothing_type",
    "clothing_color",
    "clothing_pattern",
    "clothing_neckline",
    "clothing_storage",
    "clothing_occasion",
  ] as const;
  const { data: clothingFieldOptions } = useQuery({
    queryKey: ["user_field_options", userId, "__clothing_bulk__"],
    enabled: open,
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_field_options")
        .select("field_name, custom_value")
        .eq("user_id", userId)
        .in("field_name", CLOTHING_FIELD_NAMES as unknown as string[])
        .order("created_at", { ascending: false });
      if (error) throw new Error(error.message || "Network error");
      return (data ?? []) as { field_name: string; custom_value: string }[];
    },
  });
  const optionsFor = (field: string): string[] =>
    (clothingFieldOptions ?? [])
      .filter((r) => r.field_name === field)
      .map((r) => r.custom_value);
  const typeOptions = optionsFor("clothing_type");
  const colorOptions = optionsFor("clothing_color");
  const patternOptions = optionsFor("clothing_pattern");
  const necklineOptions = optionsFor("clothing_neckline");
  const storageOptions = optionsFor("clothing_storage");
  const occasionOptions = optionsFor("clothing_occasion");

  const m = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from("clothing_items").insert({
        user_id: userId,
        name: f.name,
        photo_url: f.photo_url || null,
        clothing_type: f.clothing_type || null,
        primary_color: f.primary_color || null,
        secondary_color: f.secondary_color || null,
        pattern: f.pattern || null,
        neckline: f.neckline || null,
        price: f.price ? Number(f.price) : null,
        storage_location: f.storage_location || null,
        occasion: f.occasion || null,
        is_favorite: f.is_favorite,
        is_wishlist: true,
      } as never);
      if (error) throw new Error(error.message || "Network error");
    },
    onSuccess: () => {
      toast.success("Added to your collection.", {
        description: "Every great collection begins with a single piece. Yours just did.",
      });
      track("item_added", { item_type: "clothing" });
      qc.invalidateQueries({ queryKey: ["clothing_items", userId] });
      qc.invalidateQueries({ queryKey: ["clothing_favorites", userId] });
      setF({ ...EMPTY_CLOTHING });
      onDone();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <DialogContent className="max-w-lg max-h-[85vh] sm:max-h-[80vh] p-0 gap-0 flex flex-col overflow-hidden">
      <DialogHeader className="shrink-0 px-6 pt-6 pb-4 border-b border-border bg-background">
        <DialogTitle className="font-serif-display text-3xl">Add Garment</DialogTitle>
      </DialogHeader>
      <KeyboardAwareFormBody>
        <Row label="Name">
          <Input value={f.name} onChange={(e) => set("name", e.target.value)} maxLength={120} />
        </Row>
        <Row label="Photo">
          <p
            className="italic text-center mb-2"
            style={{ fontSize: 12, color: "var(--caileve-muted)", fontFamily: "'Cormorant Garamond', Georgia, serif" }}
          >
            Your photo is stored privately — only you can see it.
          </p>
          <PhotoUpload
            userId={userId}
            folder="clothing"
            value={f.photo_url || null}
            onChange={(p) => set("photo_url", p ?? "")}
          />
        </Row>
        <div className="grid grid-cols-2 gap-3">
          <Row label="Type">
            <SmartSelect userId={userId} fieldName="clothing_type" presets={CLOTHING_TYPE_PRESETS} customOptions={typeOptions} value={f.clothing_type} onChange={(v) => set("clothing_type", v)} />
          </Row>
          <Row label="Color">
            <SmartMultiSelect
              userId={userId}
              fieldName="clothing_color"
              presets={CLOTHING_COLOR_PRESETS}
              customOptions={colorOptions}
              max={2}
              placeholder="Select up to 2…"
              values={[f.primary_color, f.secondary_color].filter(Boolean) as string[]}
              onChange={(vs) => {
                set("primary_color", vs[0] ?? "");
                set("secondary_color", vs[1] ?? "");
              }}
            />
          </Row>
          <Row label="Pattern">
            <SmartSelect userId={userId} fieldName="clothing_pattern" presets={CLOTHING_PATTERN_PRESETS} customOptions={patternOptions} value={f.pattern} onChange={(v) => set("pattern", v)} />
          </Row>
          <Row label="Neckline">
            <SmartSelect userId={userId} fieldName="clothing_neckline" presets={CLOTHING_NECKLINE_PRESETS} customOptions={necklineOptions} value={f.neckline} onChange={(v) => set("neckline", v)} />
          </Row>
          <Row label="Storage Location">
            <SmartSelect userId={userId} fieldName="clothing_storage" presets={CLOTHING_STORAGE_PRESETS} customOptions={storageOptions} value={f.storage_location} onChange={(v) => set("storage_location", v)} />
          </Row>
          <Row label="Occasion">
            <SmartSelect userId={userId} fieldName="clothing_occasion" presets={SHARED_OCCASION_PRESETS} customOptions={occasionOptions} value={f.occasion} onChange={(v) => set("occasion", v)} />
          </Row>
        </div>
        <Row label="Price">
          <Input type="number" value={f.price} onChange={(e) => set("price", e.target.value)} />
        </Row>
      </KeyboardAwareFormBody>
      <DialogFooter className="shrink-0 px-6 py-4 border-t border-border bg-background">
        <Button
          onClick={() => {
            if (!f.name.trim()) return toast.error("Give this piece a name to save it.");
            m.mutate();
          }}
          disabled={m.isPending}
          className="bg-[var(--caileve-lilac)] text-[var(--caileve-bg)] hover:bg-[var(--caileve-lilac)]/90"
        >
          {m.isPending ? "Saving…" : "Add to Collection"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <Label className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
        {label}
      </Label>
      {children}
    </div>
  );
}

function compositionLine(wearCount: number, hasType: boolean, archetypePrimary: string | null): string | null {
  if (wearCount > 0) return `Worn ${wearCount} ${wearCount === 1 ? "time" : "times"}`;
  if (hasType) return "Newly added";
  if (archetypePrimary) return `Part of your ${archetypePrimary} signature`;
  return null;
}

function ClothingGridCard({
  item: c,
  visibleFields,
  formatFieldValue,
  wearCount,
  onToggleFav,
  onEdit,
  onDelete,
}: {
  item: ClothingItem;
  visibleFields: string[];
  formatFieldValue: (c: ClothingItem, k: string) => string | null;
  wearCount: number;
  onToggleFav: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const reveal = useCardReveal();
  const { data: profile } = useProfile();
  const hasId = !!c.id;
  return (
    <div ref={reveal.rootRef} {...reveal.bind} className="group relative" style={{ border: "1px solid var(--caileve-gold-12)", boxShadow: "var(--caileve-shadow-card)", borderRadius: "var(--caileve-radius-md)" }}>
      {hasId ? (
        <Link
          to="/clothing/$id"
          params={{ id: c.id }}
          className="block cursor-pointer"
          aria-label={`View ${c.name} details`}
        >
          <ImageBox
            src={c.prefer_clean_bg && c.photo_url_no_bg ? c.photo_url_no_bg : c.photo_url}
            alt={c.name}
            style={c.prefer_clean_bg && c.photo_url_no_bg ? { backgroundColor: bgColorFor(c.clean_bg_color) } : undefined}
          />
          <div className="mt-3 pb-3 px-1">
            <p className="font-serif-display text-xl leading-tight line-clamp-2">{c.name}</p>
            {c.clothing_type && (
              <p className="uppercase mt-1" style={{ fontSize: 10, letterSpacing: "0.25em", color: "var(--caileve-muted)" }}>
                {c.clothing_type}
              </p>
            )}
            {(() => {
              const line = compositionLine(wearCount, !!c.clothing_type, profile?.style_archetype_primary ?? null);
              return line ? (
                <p className="mt-1.5" style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "var(--caileve-muted)", fontStyle: "italic" }}>
                  {line}
                </p>
              ) : null;
            })()}
          </div>
        </Link>
      ) : (
        <button
          type="button"
          onClick={() => toast.error("This item is missing an ID and can't be opened.")}
          className="block w-full text-left cursor-pointer"
        >
          <ImageBox
            src={c.prefer_clean_bg && c.photo_url_no_bg ? c.photo_url_no_bg : c.photo_url}
            alt={c.name}
            style={c.prefer_clean_bg && c.photo_url_no_bg ? { backgroundColor: bgColorFor(c.clean_bg_color) } : undefined}
          />
          <div className="mt-3 pb-3 px-1">
            <p className="font-serif-display text-xl leading-tight line-clamp-2">{c.name}</p>
            {c.clothing_type && (
              <p className="uppercase mt-1" style={{ fontSize: 10, letterSpacing: "0.25em", color: "var(--caileve-muted)" }}>
                {c.clothing_type}
              </p>
            )}
            {(() => {
              const line = compositionLine(wearCount, !!c.clothing_type, profile?.style_archetype_primary ?? null);
              return line ? (
                <p className="mt-1.5" style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "var(--caileve-muted)", fontStyle: "italic" }}>
                  {line}
                </p>
              ) : null;
            })()}
          </div>
        </button>
      )}
      <div className="absolute top-2.5 right-2.5 z-10">
        <FavoriteHeart active={!!c.is_favorite} onToggle={onToggleFav} />
        
      </div>
      <CardRevealOverlay revealed={reveal.revealed} onEdit={onEdit} onDelete={onDelete} />
    </div>
  );
}
function CollectionSkeletonGrid({ count = 8 }: { count?: number }) {
  return (
    <div className="grid grid-cols-2 gap-6">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i}>
          <div
            className="animate-pulse w-full"
            style={{ aspectRatio: "1 / 1", backgroundColor: "var(--caileve-soft)" }}
          />
          <div
            className="animate-pulse mt-3"
            style={{ height: 16, width: "70%", backgroundColor: "var(--caileve-soft)" }}
          />
          <div
            className="animate-pulse mt-2"
            style={{ height: 10, width: "40%", backgroundColor: "var(--caileve-soft)" }}
          />
        </div>
      ))}
    </div>
  );
}
